import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import {
  getBasicFilteringOptions,
  getClearTeamModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import { generateNoDataValues } from "../../common/utils";
import TableComponent from "../../components/common/TableComponent";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import ModalBackground from "../../components/common/ModalBackground";
import ManageTeamMember from "./components/ManageTeamMember";

export default function TeamsList() {
  const {
    teamsData,
    teamModel,
    setTeamModel,
    toggleVisible,
    TEAMS_API,
    config,
    pageIndexTeam,
    setPageIndexTeam,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [teamDataFiltered, setTeamDataFiltered] = useState(teamsData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamHasChanged, setTeamHasChanged] = useState(false);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const teamsDataObj = teamsData.Data.find(
        (teamObj) => teamObj.id === Number(id)
      );
      setTeamModel(teamsDataObj);
    }
  }, [id]);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    setTeamDataFiltered(teamsData.Data);
  }, [teamsData]);

  const validateOperationResult = (result, isDelete) => {
    setTeamModel(getClearTeamModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([TEAMS_API, config]);
    if (isDelete) {
      if (pageIndexTeam !== 0 && teamModel.Data.length === 1) {
        setPageIndexTeam(pageIndexTeam - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${TEAMS_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${TEAMS_API}/${data.id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(`${`${TEAMS_API}`}`, data, config);
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !teamModel?.Name || teamModel?.Name.replaceAll(" ", "").length <= 0;

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showAddTeamMemberModal && (
        <ModalBackground
          hideCancelButton
          title={localize.manage_team_member}
          showModal={showAddTeamMemberModal}
          setShowModal={setShowAddTeamMemberModal}
          okButtonText={localize.close}
          onClickToCancel={() => {
            setShowAddTeamMemberModal(false);
          }}
          onClickToSubmit={() => {
            setShowAddTeamMemberModal(false);
            if (teamHasChanged) {
              window.location.reload(false);
            }
          }}
          Component={<ManageTeamMember setTeamHasChanged={setTeamHasChanged} />}
        />
      )}

      {showEditModal && (
        <ModalBackground
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_team}
          onClickToSubmit={async () => {
            await handleUpdateRequest(teamModel);
            setShowEditModal(false);
            setTeamModel(getClearTeamModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setTeamModel(getClearTeamModel());
          }}
          Component={
            <AddOrEditBasicEntity
              nameMandatory
              hideDescription
              model={teamModel}
              setModel={setTeamModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.work_teams}
            subtitle={localize.work_team_description}
          />

          <SearcherAndAdd
            entityName="team_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_team}
                onClickToSubmit={async () => {
                  await handleCreateRequest(teamModel);
                  setShowAddModal(false);
                  setTeamModel(getClearTeamModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setTeamModel(getClearTeamModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    nameMandatory
                    hideDescription
                    model={teamModel}
                    setModel={setTeamModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={teamModel?.Name}
          entityType={localize.work_team}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setTeamModel(getClearTeamModel());
            setShowDeleteModal(false);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(teamModel?.id);
            setShowDeleteModal(false);
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexTeam}
            setPageIndex={setPageIndexTeam}
            dataArray={teamsData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={[
          localize.name,
          localize.members,
          localize.modification_date,
          localize.modified_by,
        ]}
        results={
          teamsData.Data?.length !== 0
            ? teamDataFiltered.map((item) => ({
                key: item.id,
                data: [
                  <div>
                    <ActionButtons
                      detailsButtonText={localize.details}
                      onClickForDetailButton={() => {
                        setTeamModel(item);
                        setTeamHasChanged(false);
                        setShowAddTeamMemberModal(true);
                      }}
                      onClickForEditButton={() => {
                        setTeamModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setTeamModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  item.members?.length ?? 0,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(4)]
        }
      />
    </div>
  );
}
